import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import Confetti from "react-confetti";
import useWindowSize from "react-use/lib/useWindowSize";
import "./App.css";
import ChoiceForm from "./components/ChoiceForm";
import WheelSection from "./components/WheelSection.js";
import HistorySection from "./components/HistorySection";
import ImportExport from "./components/ImportExport";

function App() {
  const [choices, setChoices] = useState(() => {
    const savedChoices = localStorage.getItem("choices");
    return savedChoices ? JSON.parse(savedChoices) : [];
  });
  const [newChoice, setNewChoice] = useState("");
  const [spinning, setSpinning] = useState(false);
  const [selectedChoiceIndex, setSelectedChoiceIndex] = useState(null);
  const [showWinner, setShowWinner] = useState(false);
  const [spinDuration] = useState(13000);
  const [wheelColors] = useState([]);
  const [theme, setTheme] = useState("light");
  const [winnerHistory, setWinnerHistory] = useState(() => {
    const savedWinners = localStorage.getItem("winnerHistory");
    return savedWinners ? JSON.parse(savedWinners) : [];
  });
  const { width, height } = useWindowSize();

  useEffect(() => {
    localStorage.setItem("choices", JSON.stringify(choices));
  }, [choices]);

  useEffect(() => {
    localStorage.setItem("winnerHistory", JSON.stringify(winnerHistory));
  }, [winnerHistory]);

  const handleAddChoice = () => {
    const newChoices = newChoice
      .split(",")
      .map((choice) => choice.trim())
      .filter((choice) => choice !== "");
    const uniqueNewChoices = newChoices.filter(
      (choice) => !choices.includes(choice)
    );
    if (uniqueNewChoices.length > 0) {
      setChoices([...choices, ...uniqueNewChoices]);
      setNewChoice("");
    } else {
      alert("Some or all of these choices already exist or are invalid.");
    }
  };

  const handleSpin = () => {
    const prizeNumber = Math.floor(Math.random() * choices.length);
    setSelectedChoiceIndex(prizeNumber);
    setSpinning(true);
    setTimeout(() => {
      setSpinning(false);
      setShowWinner(true);
      // Add the new winner to the beginning of the history
      setWinnerHistory([choices[prizeNumber], ...winnerHistory]);
    }, spinDuration);
  };

  const handleStopSpinning = () => {
    setSpinning(false);
    setShowWinner(true);
  };

  const handleRemoveChoice = (index) => {
    setChoices(choices.filter((_, i) => i !== index));
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleAddChoice();
    }
  };

  const handleClearWinnerHistory = () => {
    setWinnerHistory([]);
  };

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    document.body.className = newTheme;
  };

  const handleExportChoices = () => {
    const dataStr =
      "data:text/json;charset=utf-8," +
      encodeURIComponent(JSON.stringify(choices));
    const downloadAnchorNode = document.createElement("a");
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", "choices.json");
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const handleImportChoices = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const importedChoices = JSON.parse(e.target.result);
        setChoices(importedChoices);
      };
      reader.readAsText(file);
    }
  };

  const handleReset = () => {
    setChoices([]);
    setWinnerHistory([]);
    setShowWinner(false);
    setSpinning(false);
    setSelectedChoiceIndex(null);
    localStorage.removeItem("choices");
    localStorage.removeItem("winnerHistory");
  };

  const handleCloseWinner = () => {
    // Remove the winner from the choices
    setChoices(choices.filter((_, index) => index !== selectedChoiceIndex));
    // Reset state
    setShowWinner(false);
    setSelectedChoiceIndex(null);
  };

  return (
    <div className={`app-container ${theme}`}>
      <header className="app-header bg-dark text-white text-center py-3 mb-4">
        <h1>Spinning Wheel</h1>
        <button onClick={toggleTheme} className="btn btn-secondary">
          Toggle Theme
        </button>
      </header>
      <main className="main-container row justify-content-center">
        <div className="col-md-6 wheel-container">
          {choices.length > 0 ? (
            <WheelSection
              choices={choices}
              spinning={spinning}
              selectedChoiceIndex={selectedChoiceIndex}
              wheelColors={wheelColors}
              handleStopSpinning={handleStopSpinning}
            />
          ) : (
            <div className="instructions">
              <p>
                <strong>• Add Choices:</strong> Enter your choices in the input
                field and press "Enter" or click the "Add" button.
                <br />
                <strong>• Spin the Wheel:</strong> Once choices are added, click
                "Spin the Wheel" to randomly select a winner.
                <br />
                <strong>• Reset Everything:</strong> Use the "Reset All" button
                to clear all choices and the winner history.
                <br />
                <strong>• Import/Export Choices:</strong> Save your choices or
                load them from a file using the import/export options.
              </p>
            </div>
          )}
        </div>
        <div className="col-md-6 form-container">
          <ChoiceForm
            newChoice={newChoice}
            setNewChoice={setNewChoice}
            handleAddChoice={handleAddChoice}
            handleKeyPress={handleKeyPress}
          />
          <button
            className="btn btn-success mb-3 mt-3 w-100"
            onClick={handleSpin}
            disabled={choices.length === 0 || spinning}
          >
            Spin the Wheel
          </button>
          <ul className="list-group mb-3">
            {choices.map((choice, index) => (
              <li
                key={index}
                className="list-group-item d-flex justify-content-between align-items-center"
              >
                {choice}
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleRemoveChoice(index)}
                >
                  &times;
                </button>
              </li>
            ))}
          </ul>
          <HistorySection
            winnerHistory={winnerHistory}
            handleClearWinnerHistory={handleClearWinnerHistory}
          />
          <ImportExport
            handleExportChoices={handleExportChoices}
            handleImportChoices={handleImportChoices}
          />
          <button className="btn btn-warning w-100 mt-3" onClick={handleReset}>
            Reset All
          </button>
        </div>
      </main>
      {showWinner && (
        <>
          <Confetti width={width} height={height} numberOfPieces={200} />
          <div
            className="winner-modal modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            style={{ display: showWinner ? "block" : "none" }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Winner</h5>
                </div>
                <div className="modal-body">
                  <p>{choices[selectedChoiceIndex]}</p>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-primary"
                    onClick={handleCloseWinner}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default App;
